import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Mail, Eye, MousePointer, TrendingUp, BarChart3, Send, Clock, Crown, Lock, AlertTriangle, ShieldCheck, ShieldAlert, LayoutTemplate, Calendar, Palette, X, Tag, DollarSign } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import api from '../../lib/api';
import ProducerLayout from '../../components/producer/ProducerLayout';
import { checkSpamScore } from '../../lib/spamScore';
import { EMAIL_TEMPLATES, renderEmailHtml, TEMPLATE_FIELDS, getEmailTemplate } from '../../lib/emailTemplates';
import { extractDominantColor } from '../../lib/colorExtractor';
import { BrandingPanel } from '../../components/email/BrandingPanel';
import { DynamicBlocksPanel } from '../../components/email/DynamicBlocksPanel';
import { SavedTemplatesBar } from '../../components/email/SavedTemplatesBar';
import { EmailPreviewFrame } from '../../components/email/EmailPreviewFrame';
import { TestEmailButton } from '../../components/email/TestEmailButton';

const StatCard = ({ icon: Icon, label, value, subtext, color = "text-violet-400" }) => (
  <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6 hover:border-violet-500/30 transition-all">
    <div className="flex items-center gap-3 mb-3">
      <div className={`p-2 rounded-lg bg-violet-500/10 ${color}`}>
        <Icon className="w-5 h-5" />
      </div>
      <span className="text-zinc-400 text-sm">{label}</span>
    </div>
    <div className="text-3xl font-bold text-white">{value}</div>
    {subtext && <div className="text-sm text-zinc-500 mt-1">{subtext}</div>}
  </div>
);

const SpamScorePanel = ({ campaign }) => {
  const spam = useMemo(() => checkSpamScore(campaign), [campaign]);
  const empty = !campaign.subject?.trim() && !campaign.body?.trim();
  if (empty) return null;

  const config = {
    low:    { bg: 'bg-emerald-500/10', border: 'border-emerald-500/30', text: 'text-emerald-300', label: 'Listo para enviar', Icon: ShieldCheck, bar: 'bg-emerald-500' },
    medium: { bg: 'bg-amber-500/10',   border: 'border-amber-500/30',   text: 'text-amber-300',   label: 'Revisar antes',      Icon: ShieldAlert, bar: 'bg-amber-500' },
    high:   { bg: 'bg-red-500/10',     border: 'border-red-500/40',    text: 'text-red-300',     label: 'Alto riesgo de spam', Icon: AlertTriangle, bar: 'bg-red-500' },
  }[spam.level];
  const { Icon } = config;
  const visibleIssues = spam.issues.slice(0, 4);
  const extra = spam.issues.length - visibleIssues.length;

  return (
    <div
      data-testid="spam-score-panel"
      data-spam-level={spam.level}
      data-spam-score={spam.score}
      className={`rounded-lg border p-3 space-y-2 ${config.bg} ${config.border}`}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Icon size={16} className={config.text} />
          <span className={`text-xs font-bold uppercase tracking-wider ${config.text}`}>{config.label}</span>
        </div>
        <span className={`text-xs font-mono font-bold ${config.text}`}>{spam.score}/100</span>
      </div>
      <div className="w-full h-1.5 bg-zinc-800 rounded-full overflow-hidden">
        <div className={`h-full ${config.bar} transition-all`} style={{ width: `${spam.score}%` }} />
      </div>
      {visibleIssues.length > 0 && (
        <ul className="space-y-1 mt-1">
          {visibleIssues.map((iss, i) => (
            <li key={i} className="text-[11px] text-zinc-300 leading-snug flex items-start gap-1.5">
              <span className={`inline-block mt-0.5 w-1 h-1 rounded-full flex-shrink-0 ${
                iss.severity === 'error' ? 'bg-red-400' : iss.severity === 'warn' ? 'bg-amber-400' : 'bg-zinc-500'
              }`} />
              <span>{iss.message}</span>
            </li>
          ))}
          {extra > 0 && (
            <li className="text-[11px] text-zinc-500 italic">+ {extra} avisos más…</li>
          )}
        </ul>
      )}
      {spam.issues.length === 0 && (
        <p className="text-[11px] text-emerald-200/80">Tu email está limpio. Buena tasa de entrega esperada.</p>
      )}
    </div>
  );
};

const EmailTypeCard = ({ type, data }) => {
  const openRate = data.sent > 0 ? ((data.opened / data.sent) * 100).toFixed(1) : 0;
  const clickRate = data.sent > 0 ? ((data.clicked / data.sent) * 100).toFixed(1) : 0;
  
  const typeLabels = {
    'ticket_purchase': 'Compra de Tickets',
    'event_reminder': 'Recordatorio de Evento',
    'event_update': 'Actualización de Evento',
    'newsletter': 'Newsletter',
    'promotion': 'Promoción',
    'other': 'Otros'
  };
  
  return (
    <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-5 hover:border-violet-500/30 transition-all">
      <h4 className="text-white font-medium mb-4">{typeLabels[type] || type}</h4>
      <div className="grid grid-cols-3 gap-4 text-center">
        <div>
          <div className="text-2xl font-bold text-white">{data.sent}</div>
          <div className="text-xs text-zinc-500">Enviados</div>
        </div>
        <div>
          <div className="text-2xl font-bold text-emerald-400">{openRate}%</div>
          <div className="text-xs text-zinc-500">Abiertos</div>
        </div>
        <div>
          <div className="text-2xl font-bold text-pink-400">{clickRate}%</div>
          <div className="text-xs text-zinc-500">Clicks</div>
        </div>
      </div>
    </div>
  );
};

const ProducerEmailMetricsPage = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState(30);
  const [subscription, setSubscription] = useState(null);
  const [producer, setProducer] = useState(null);
  const [quota, setQuota] = useState(null);
  const [showCompose, setShowCompose] = useState(false);
  const [campaign, setCampaign] = useState({ subject: '', body: '', audience: 'all_buyers' });
  // Structured content (driven by the selected template) — what the producer actually edits.
  const [selectedTemplateId, setSelectedTemplateId] = useState('luxury_event');
  const [content, setContent] = useState(getEmailTemplate('luxury_event')?.defaults || {});
  const [sending, setSending] = useState(false);
  const [events, setEvents] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState('');
  const [scheduledList, setScheduledList] = useState([]);
  const [scheduleMode, setScheduleMode] = useState(false);
  const [scheduledFor, setScheduledFor] = useState('');
  const [audienceCounts, setAudienceCounts] = useState(null);
  const [conversions, setConversions] = useState(null);
  const [discountEnabled, setDiscountEnabled] = useState(false);
  const [discountPercent, setDiscountPercent] = useState(15);
  const [discountHours, setDiscountHours] = useState(48);
  const [searchParams, setSearchParams] = useSearchParams();

  // Whenever template or content changes, re-render the final HTML into campaign.body
  useEffect(() => {
    let html = renderEmailHtml(selectedTemplateId, content);
    // Inject a preview discount banner so producer can see how it looks before sending
    if (discountEnabled && selectedEventId) {
      const banner = `<table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="margin:0;"><tr><td style="padding:0 32px 32px;"><div style="background:linear-gradient(135deg,#1a1a1a 0%,#2a2a2a 100%);border:2px dashed rgba(212,175,55,0.5);border-radius:12px;padding:20px;text-align:center;"><div style="font-size:10px;letter-spacing:3px;text-transform:uppercase;color:rgba(255,255,255,0.5);margin-bottom:8px;">Tu código de regalo</div><div style="font-size:32px;font-weight:900;letter-spacing:4px;color:#D4AF37;font-family:'Courier New',monospace;margin-bottom:6px;">PROMO••••••</div><div style="font-size:14px;color:#fff;font-weight:600;">${discountPercent}% OFF · válido ${discountHours === 168 ? '7 días' : discountHours + 'hs'}</div></div></td></tr></table>`;
      // Inject at the deterministic marker rendered by the template engine
      html = html.replace('<!--DISCOUNT_BLOCK-->', banner);
    }
    setCampaign(c => ({ ...c, body: html }));
  }, [selectedTemplateId, content, discountEnabled, discountPercent, discountHours, selectedEventId]);

  const fetchAudienceCounts = async (evtId) => {
    try {
      const r = await api.get(`/producers/me/email-campaigns/audience-counts${evtId ? `?event_id=${evtId}` : ''}`);
      setAudienceCounts(r.data);
    } catch (_) {
      setAudienceCounts(null);
    }
  };

  const handleSelectEvent = async (eventId) => {
    setSelectedEventId(eventId);
    fetchAudienceCounts(eventId);
    if (!eventId) {
      setContent(c => ({ ...c, accent_override: undefined, event_image: undefined }));
      setCampaign(c => ({ ...c, audience: c.audience === 'event_buyers' ? 'all_buyers' : c.audience }));
      return;
    }
    // When linking an event, prefer event_buyers as the default audience
    setCampaign(c => ({ ...c, audience: 'event_buyers' }));
    const ev = events.find(e => e.id === eventId);
    if (!ev) return;
    // Pre-fill content with event data
    const dateStr = ev.date ? new Date(ev.date).toLocaleDateString('es-AR', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';
    setCampaign(c => ({ ...c, subject: c.subject || `${ev.title} — ${dateStr}` }));
    setContent(c => ({
      ...c,
      title: ev.title || c.title,
      subtitle: dateStr || c.subtitle,
      event_image: ev.image_url || undefined,
      cta_text: c.cta_text || 'Comprar entradas',
      cta_url: ev.slug ? `${window.location.origin}/evento/${ev.slug}` : (ev.id ? `${window.location.origin}/evento/${ev.id}` : c.cta_url),
    }));
    // Extract color from event image
    if (ev.image_url) {
      try {
        const color = await extractDominantColor(ev.image_url);
        setContent(c => ({ ...c, accent_override: color }));
        toast.success(`Paleta del evento aplicada: ${color}`);
      } catch (_) {
        // ignore extraction errors
      }
    }
  };
  
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [statsRes, subRes, prodRes, quotaRes, eventsRes, schedRes] = await Promise.all([
          api.get(`/analytics/email/stats?days=${period}`),
          api.get('/producers/me/subscription').catch(() => ({ data: null })),
          api.get('/producers/me').catch(() => ({ data: null })),
          api.get('/producers/me/email-campaigns/quota').catch(() => ({ data: null })),
          api.get('/producers/me/events').catch(() => ({ data: null })),
          api.get('/producers/me/email-campaigns/scheduled').catch(() => ({ data: null })),
        ]);
        setStats(statsRes.data);
        setSubscription(subRes.data?.subscription || subRes.data);
        setProducer(prodRes.data);
        setQuota(quotaRes.data);
        const evs = eventsRes.data?.events || eventsRes.data || [];
        setEvents(Array.isArray(evs) ? evs.filter(e => e.status === 'published' || e.status === 'active') : []);
        setScheduledList(schedRes.data?.scheduled || []);
        // Initial audience counts (no event scoped)
        try {
          const aRes = await api.get('/producers/me/email-campaigns/audience-counts');
          setAudienceCounts(aRes.data);
        } catch (_) { /* ignore */ }
        try {
          const cRes = await api.get('/producers/me/email-campaigns/conversions');
          setConversions(cRes.data);
        } catch (_) { /* ignore */ }
      } catch (error) {
        console.error('Error fetching email stats:', error);
        setStats({
          total_sent: 0, total_opened: 0, total_clicked: 0,
          open_rate: 0, click_rate: 0, click_to_open_rate: 0, by_type: {}
        });
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [period]);
  
  const isPremium = !!subscription?.is_premium;
  
  // Auto-open composer from query string (e.g. ?compose=1&event=evt_xxx coming from event page)
  useEffect(() => {
    if (loading) return;
    const wantsCompose = searchParams.get('compose') === '1';
    const evtId = searchParams.get('event');
    if (wantsCompose && isPremium) {
      setShowCompose(true);
      if (evtId && events.find(e => e.id === evtId)) {
        handleSelectEvent(evtId);
      }
      // Clean the URL so refreshing doesn't keep reopening
      searchParams.delete('compose');
      searchParams.delete('event');
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, events]);
  
  const handleSendCampaign = async () => {
    if (!campaign.subject.trim() || !campaign.body.trim()) {
      toast.error('Completá asunto y cuerpo del email');
      return;
    }
    const spam = checkSpamScore(campaign);
    if (spam.level === 'high') {
      const proceed = window.confirm(
        `Spam score: ${spam.score}/100 — ALTO RIESGO.\n\nProblemas: ${spam.issues.filter(i => i.severity !== 'info').map(i => '· ' + i.message).join('\n')}\n\n¿Querés enviar igual?`
      );
      if (!proceed) return;
    }
    setSending(true);
    try {
      // Scheduled mode requires a future datetime
      if (scheduleMode) {
        if (!scheduledFor) {
          toast.error('Elegí fecha y hora de envío');
          setSending(false);
          return;
        }
        const dt = new Date(scheduledFor);
        if (isNaN(dt) || dt <= new Date(Date.now() + 60000)) {
          toast.error('La fecha debe ser al menos 1 minuto en el futuro');
          setSending(false);
          return;
        }
      }
      const payload = {
        ...campaign,
        event_id: selectedEventId || undefined,
        scheduled_for: scheduleMode && scheduledFor ? new Date(scheduledFor).toISOString() : undefined,
        discount_enabled: discountEnabled && !!selectedEventId,
        discount_percent: discountPercent,
        discount_hours_valid: discountHours,
      };
      const r = await api.post('/producers/me/email-campaigns/send', payload);
      if (r.data?.scheduled) {
        toast.success(r.data.message || 'Campaña programada');
        const s = await api.get('/producers/me/email-campaigns/scheduled').catch(() => ({ data: null }));
        setScheduledList(s.data?.scheduled || []);
      } else {
        toast.success(`Campaña enviada: ${r.data.sent}/${r.data.total_recipients}`);
        const q = await api.get('/producers/me/email-campaigns/quota').catch(() => ({ data: null }));
        setQuota(q.data);
      }
      setShowCompose(false);
      setScheduleMode(false);
      setScheduledFor('');
      setDiscountEnabled(false);
      setCampaign({ subject: '', body: '', audience: 'all_buyers' });
      setContent(getEmailTemplate(selectedTemplateId)?.defaults || {});
      setSelectedEventId('');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al enviar campaña');
    } finally {
      setSending(false);
    }
  };

  const handleCancelScheduled = async (sid) => {
    if (!window.confirm('¿Cancelar esta campaña programada?')) return;
    try {
      await api.delete(`/producers/me/email-campaigns/scheduled/${sid}`);
      setScheduledList(list => list.filter(s => s.id !== sid));
      toast.success('Campaña cancelada');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo cancelar');
    }
  };
  
  if (loading) {
    return (
      <ProducerLayout currentPage="email-metrics">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-500"></div>
        </div>
      </ProducerLayout>
    );
  }
  
  return (
    <ProducerLayout currentPage="email-metrics">
      <div className="p-6 md:p-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-xl bg-gradient-to-br from-violet-500/20 to-pink-500/20 border border-violet-500/30">
              <Mail className="w-8 h-8 text-violet-400" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-white">MÉTRICAS DE EMAIL</h1>
              <p className="text-zinc-400">Analiza el rendimiento de tus comunicaciones</p>
            </div>
          </div>
          
          {/* Period selector */}
          <div className="flex items-center gap-2 bg-zinc-900/50 border border-zinc-800 rounded-lg p-1">
            {[7, 30, 90].map((days) => (
              <button
                key={days}
                onClick={() => setPeriod(days)}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                  period === days 
                    ? 'bg-gradient-to-r from-violet-600 to-pink-600 text-white' 
                    : 'text-zinc-400 hover:text-white'
                }`}
              >
                {days} días
              </button>
            ))}
          </div>
        </div>
        
        {/* Main Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard 
            icon={Send} 
            label="Emails Enviados" 
            value={stats?.total_sent || 0}
            subtext={`Últimos ${period} días`}
          />
          <StatCard 
            icon={Eye} 
            label="Tasa de Apertura" 
            value={`${stats?.open_rate || 0}%`}
            subtext={`${stats?.total_opened || 0} abiertos`}
            color="text-green-400"
          />
          <StatCard 
            icon={MousePointer} 
            label="Tasa de Clicks" 
            value={`${stats?.click_rate || 0}%`}
            subtext={`${stats?.total_clicked || 0} clicks`}
            color="text-blue-400"
          />
          <StatCard 
            icon={TrendingUp} 
            label="Click / Apertura" 
            value={`${stats?.click_to_open_rate || 0}%`}
            subtext="De los que abrieron"
            color="text-purple-400"
          />
        </div>
        
        {/* Campaign Conversions */}
        {isPremium && conversions && conversions.campaigns.length > 0 && (
          <div className="mb-6 rounded-xl border border-amber-500/30 bg-gradient-to-br from-amber-500/5 to-amber-900/5 p-4" data-testid="conversions-section">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <h4 className="text-sm font-bold text-amber-200 flex items-center gap-2">
                <DollarSign size={14} /> Conversiones de campaña
              </h4>
              <div className="flex gap-4 text-[11px]">
                <span className="text-zinc-400">Total usos: <strong className="text-white">{conversions.total_uses}</strong></span>
                <span className="text-zinc-400">Ingresos: <strong className="text-emerald-300">${conversions.total_revenue.toLocaleString('es-AR')}</strong></span>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-[10px] uppercase tracking-wider text-zinc-500 border-b border-zinc-800">
                    <th className="text-left font-medium py-2">Campaña</th>
                    <th className="text-left font-medium py-2">Código</th>
                    <th className="text-right font-medium py-2">Enviados</th>
                    <th className="text-right font-medium py-2">Usos</th>
                    <th className="text-right font-medium py-2">Conv.</th>
                    <th className="text-right font-medium py-2">Ingresos</th>
                    <th className="text-right font-medium py-2">Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {conversions.campaigns.map(c => (
                    <tr key={c.campaign_id} className="border-b border-zinc-800/50">
                      <td className="py-2 text-white truncate max-w-[200px]">{c.subject}</td>
                      <td className="py-2"><code className="text-amber-300 font-mono">{c.code}</code> <span className="text-zinc-500">-{c.discount_percent}%</span></td>
                      <td className="py-2 text-right text-zinc-300">{c.recipients}</td>
                      <td className="py-2 text-right text-white font-bold">{c.uses}</td>
                      <td className="py-2 text-right">
                        <span className={`px-1.5 py-0.5 rounded text-[10px] ${c.conversion_rate >= 10 ? 'bg-emerald-500/20 text-emerald-300' : c.conversion_rate >= 3 ? 'bg-amber-500/20 text-amber-300' : 'bg-zinc-700/40 text-zinc-400'}`}>{c.conversion_rate}%</span>
                      </td>
                      <td className="py-2 text-right text-emerald-300 font-mono">${c.revenue.toLocaleString('es-AR')}</td>
                      <td className="py-2 text-right">
                        <span className={`text-[10px] uppercase tracking-wider ${c.expired ? 'text-zinc-500' : 'text-emerald-400'}`}>{c.expired ? 'Vencido' : 'Activo'}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Scheduled campaigns list */}
        {isPremium && scheduledList.length > 0 && (
          <div className="mb-6 rounded-xl border border-violet-500/30 bg-violet-500/5 p-4" data-testid="scheduled-list">
            <h4 className="text-sm font-semibold text-violet-200 flex items-center gap-2 mb-3">
              <Calendar size={14} /> Campañas programadas ({scheduledList.length})
            </h4>
            <div className="space-y-2">
              {scheduledList.map(s => (
                <div key={s.id} className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg bg-black/30 border border-violet-500/20">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-white truncate">{s.subject}</div>
                    <div className="text-[11px] text-zinc-400">
                      Envío: {new Date(s.scheduled_for).toLocaleString('es-AR', { dateStyle: 'medium', timeStyle: 'short' })}
                    </div>
                  </div>
                  <button
                    onClick={() => handleCancelScheduled(s.id)}
                    className="text-zinc-400 hover:text-red-400 transition-colors"
                    data-testid={`cancel-scheduled-${s.id}`}
                    title="Cancelar"
                  >
                    <X size={14} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Send Campaign CTA */}
        <div className={`mb-8 rounded-xl p-5 border ${isPremium ? 'bg-gradient-to-r from-violet-500/10 to-pink-500/10 border-violet-500/30' : 'bg-zinc-900/60 border-amber-500/40'}`} data-testid="email-campaign-section">
          <div className="flex items-start gap-3 flex-wrap">
            <div className={`p-2 rounded-lg ${isPremium ? 'bg-violet-500/20' : 'bg-amber-500/10'}`}>
              {isPremium ? <Send size={20} className="text-violet-300" /> : <Lock size={20} className="text-amber-400" />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="text-white font-semibold">Enviar campaña a tus compradores</h3>
                {!isPremium && (
                  <span className="text-[10px] uppercase tracking-wider text-amber-400 bg-amber-500/10 border border-amber-500/30 rounded-full px-2 py-0.5 flex items-center gap-1">
                    <Crown size={10} /> Super Productora
                  </span>
                )}
                {isPremium && quota && (
                  <span
                    data-testid="daily-quota-badge"
                    className={`text-[10px] uppercase tracking-wider rounded-full px-2 py-0.5 flex items-center gap-1 ${quota.remaining > 0 ? 'text-emerald-300 bg-emerald-500/10 border border-emerald-500/30' : 'text-red-300 bg-red-500/10 border border-red-500/40'}`}
                  >
                    Hoy {quota.sent_today}/{quota.daily_limit}
                  </span>
                )}
                {isPremium && quota && quota.credits > 0 && (
                  <span
                    data-testid="campaign-credits-badge"
                    className="text-[10px] uppercase tracking-wider text-[#D4AF37] bg-[#D4AF37]/10 border border-[#D4AF37]/40 rounded-full px-2 py-0.5 flex items-center gap-1 font-bold"
                    title="Créditos otorgados por el equipo de Bravo"
                  >
                    🎁 {quota.credits} crédito{quota.credits === 1 ? '' : 's'}
                  </span>
                )}
              </div>
              <p className="text-sm text-zinc-400 mt-1">
                {isPremium
                  ? 'Enviá newsletters, recordatorios o promos a tu base de compradores. Límite: 1 campaña por día.'
                  : 'Esta función es exclusiva de Super Productora. Suscribite para enviar emails a tus compradores y promocionar tus eventos.'}
              </p>
            </div>
            <div>
              {isPremium ? (
                <button
                  onClick={() => setShowCompose(v => !v)}
                  disabled={quota && quota.remaining === 0 && !showCompose}
                  className="px-4 py-2 bg-gradient-to-r from-violet-600 to-pink-600 hover:from-violet-700 hover:to-pink-700 disabled:opacity-40 disabled:cursor-not-allowed text-white rounded-lg font-medium text-sm flex items-center gap-2"
                  data-testid="open-compose-btn"
                  title={quota && quota.remaining === 0 ? 'Ya enviaste tu campaña diaria. Volvé mañana.' : ''}
                >
                  <Send size={14} />
                  {showCompose ? 'Cancelar' : (quota && quota.remaining === 0 ? 'Cuota agotada' : 'Nueva campaña')}
                </button>
              ) : (
                <Link to="/producer/subscription">
                  <button className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-black rounded-lg font-bold text-sm flex items-center gap-2" data-testid="upgrade-email-btn">
                    <Crown size={14} />
                    Activar Super Productora
                  </button>
                </Link>
              )}
            </div>
          </div>
          
          {showCompose && isPremium && (
            <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="compose-form">
              {/* Composer */}
              <div className="p-4 bg-black/40 border border-zinc-800 rounded-lg space-y-3">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">Editor</span>
                  <span className="text-[10px] uppercase tracking-wider text-violet-400">{(campaign.body || '').length} caracteres</span>
                </div>

                {/* Saved templates */}
                <SavedTemplatesBar
                  apiPath="/producers/me/email-templates"
                  templateId={selectedTemplateId}
                  content={content}
                  onLoad={(t) => { setSelectedTemplateId(t.template_id); setContent(t.content || {}); }}
                />

                {/* Event picker — auto-fill content + extract brand color from event cover */}
                {events.length > 0 && (
                  <div>
                    <label className="text-xs text-zinc-400 uppercase flex items-center gap-1.5 mb-2">
                      <Palette size={11} className="text-emerald-400" />
                      Vincular a un evento (opcional)
                    </label>
                    <select
                      value={selectedEventId}
                      onChange={(e) => handleSelectEvent(e.target.value)}
                      className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm"
                      data-testid="event-picker"
                    >
                      <option value="">— Sin vincular —</option>
                      {events.map(ev => (
                        <option key={ev.id} value={ev.id}>{ev.title}{ev.date ? ` · ${new Date(ev.date).toLocaleDateString('es-AR')}` : ''}</option>
                      ))}
                    </select>
                    {content.accent_override && (
                      <div className="mt-2 flex items-center gap-2 text-[11px] text-zinc-400">
                        <span>Paleta aplicada:</span>
                        <span className="inline-flex items-center gap-1.5">
                          <span className="w-3 h-3 rounded-full border border-white/20" style={{ background: content.accent_override }} />
                          <code className="text-zinc-300">{content.accent_override}</code>
                        </span>
                      </div>
                    )}
                  </div>
                )}

                {/* Template picker */}
                <div>
                  <label className="text-xs text-zinc-400 uppercase flex items-center gap-1.5 mb-2">
                    <LayoutTemplate size={11} className="text-violet-400" />
                    Diseño del mail
                  </label>
                  <div className="grid grid-cols-5 gap-2" data-testid="template-picker">
                    {EMAIL_TEMPLATES.map(tpl => {
                      const active = tpl.id === selectedTemplateId;
                      return (
                        <button
                          key={tpl.id}
                          type="button"
                          data-testid={`template-${tpl.id}`}
                          onClick={() => {
                            setSelectedTemplateId(tpl.id);
                            // Reset content to template defaults if current content is empty/from another template
                            setContent(prev => {
                              const hasUserContent = prev && (prev.title || prev.body1);
                              if (!hasUserContent) return tpl.defaults;
                              // Keep user content but fill missing fields with template defaults
                              return { ...tpl.defaults, ...prev };
                            });
                          }}
                          className={`rounded-lg overflow-hidden border transition-all group ${active ? 'border-violet-500 ring-2 ring-violet-500/30' : 'border-zinc-700 hover:border-zinc-500'}`}
                          title={tpl.description}
                        >
                          <div className="h-10" style={{ background: tpl.swatch }} />
                          <div className="px-1 py-1 bg-zinc-900 text-[9px] text-zinc-300 group-hover:text-white truncate text-center">
                            {tpl.label}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Branding */}
                <div className="pt-2 border-t border-zinc-800">
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold mb-2">Branding y estilo</div>
                  <BrandingPanel content={content} setContent={setContent} brandingApi="/producers/me/email-branding" />
                </div>

                <div>
                  <label className="text-xs text-zinc-400 uppercase">Audiencia</label>
                  <div className="grid grid-cols-1 gap-2 mt-1" data-testid="audience-picker">
                    {[
                      {
                        id: 'event_buyers',
                        label: 'Compradores de este evento',
                        desc: 'Mandá un recordatorio, info del lugar, lineup actualizado…',
                        count: audienceCounts?.event_buyers,
                        disabled: !selectedEventId,
                        disabledMsg: 'Seleccioná un evento para usar esta opción',
                      },
                      {
                        id: 'all_buyers',
                        label: 'Todos mis compradores',
                        desc: 'Tu base completa — newsletter, comunicados generales.',
                        count: audienceCounts?.all_buyers,
                      },
                      {
                        id: 'interested_not_buyers',
                        label: 'Interesados que aún no compraron',
                        desc: 'Seguidores + clientes pasados que NO tienen entrada para este evento. Convierten 2-3x más.',
                        count: audienceCounts?.interested_not_buyers,
                        highlight: !!selectedEventId,
                      },
                    ].map(opt => {
                      const active = campaign.audience === opt.id;
                      return (
                        <button
                          key={opt.id}
                          type="button"
                          data-testid={`audience-${opt.id}`}
                          disabled={opt.disabled}
                          onClick={() => !opt.disabled && setCampaign(c => ({ ...c, audience: opt.id }))}
                          className={`relative text-left p-2.5 rounded-lg border transition-all ${active ? 'border-violet-500 bg-violet-500/10' : 'border-zinc-700 hover:border-zinc-600 bg-zinc-900/40'} ${opt.disabled ? 'opacity-40 cursor-not-allowed' : ''}`}
                          title={opt.disabled ? opt.disabledMsg : opt.desc}
                        >
                          <div className="flex items-center justify-between gap-2 mb-0.5">
                            <span className="text-xs text-white font-medium flex items-center gap-1.5">
                              {opt.label}
                              {opt.highlight && <span className="text-[8px] uppercase tracking-wider bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 rounded px-1 py-0.5">🔥 +Conv</span>}
                            </span>
                            <span className={`text-[10px] font-mono ${active ? 'text-violet-300' : 'text-zinc-500'}`}>
                              {opt.count != null ? `${opt.count} dest.` : '…'}
                            </span>
                          </div>
                          <p className="text-[10px] text-zinc-500 leading-snug">{opt.desc}</p>
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div>
                  <label className="text-xs text-zinc-400 uppercase">Asunto del email</label>
                  <input
                    type="text"
                    value={campaign.subject}
                    onChange={(e) => setCampaign(c => ({ ...c, subject: e.target.value }))}
                    placeholder="Ej: ¡Nueva fecha confirmada!"
                    className="w-full mt-1 bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm"
                    data-testid="campaign-subject"
                  />
                </div>

                {/* Structured content fields */}
                <div className="pt-2 border-t border-zinc-800 space-y-3">
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">Contenido del mail</div>
                  {TEMPLATE_FIELDS.map(field => (
                    <div key={field.key}>
                      <label className="text-xs text-zinc-400 uppercase">{field.label}</label>
                      {field.type === 'textarea' ? (
                        <textarea
                          value={content[field.key] || ''}
                          onChange={(e) => setContent(c => ({ ...c, [field.key]: e.target.value }))}
                          placeholder={field.placeholder}
                          rows={3}
                          className="w-full mt-1 bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm"
                          data-testid={`content-${field.key}`}
                        />
                      ) : (
                        <input
                          type="text"
                          value={content[field.key] || ''}
                          onChange={(e) => setContent(c => ({ ...c, [field.key]: e.target.value }))}
                          placeholder={field.placeholder}
                          className="w-full mt-1 bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm"
                          data-testid={`content-${field.key}`}
                        />
                      )}
                    </div>
                  ))}
                </div>

                {/* Dynamic content blocks */}
                <div className="pt-2 border-t border-zinc-800">
                  <DynamicBlocksPanel
                    content={content}
                    setContent={setContent}
                    events={events}
                    selectedEvent={events.find(e => e.id === selectedEventId)}
                  />
                </div>

                {/* Discount code generator — converts campaigns into measurable ROI */}
                {selectedEventId && (
                  <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 space-y-2" data-testid="discount-block">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={discountEnabled}
                        onChange={(e) => setDiscountEnabled(e.target.checked)}
                        className="w-3.5 h-3.5 accent-amber-500"
                        data-testid="discount-toggle"
                      />
                      <Tag size={13} className="text-amber-400" />
                      <span className="text-xs text-zinc-100 font-medium">Incluir código de descuento único</span>
                      <span className="ml-auto text-[9px] uppercase tracking-wider text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded px-1.5 py-0.5">+ROI</span>
                    </label>
                    {discountEnabled && (
                      <div className="pl-6 space-y-2.5">
                        <div>
                          <div className="flex items-center justify-between mb-1">
                            <label className="text-[10px] text-zinc-400 uppercase">Descuento</label>
                            <span className="text-xs text-amber-400 font-bold">{discountPercent}% OFF</span>
                          </div>
                          <input
                            type="range"
                            min={5}
                            max={50}
                            step={5}
                            value={discountPercent}
                            onChange={(e) => setDiscountPercent(parseInt(e.target.value, 10))}
                            className="w-full accent-amber-500"
                            data-testid="discount-percent"
                          />
                          <div className="flex justify-between text-[9px] text-zinc-500 mt-0.5">
                            <span>5%</span><span>25%</span><span>50%</span>
                          </div>
                        </div>
                        <div>
                          <label className="text-[10px] text-zinc-400 uppercase mb-1 block">Válido por</label>
                          <div className="grid grid-cols-4 gap-1.5">
                            {[24, 48, 72, 168].map(h => (
                              <button
                                key={h}
                                type="button"
                                onClick={() => setDiscountHours(h)}
                                data-testid={`discount-hours-${h}`}
                                className={`px-2 py-1.5 rounded-md text-[10px] uppercase tracking-wider font-semibold transition-all ${discountHours === h ? 'bg-amber-500 text-black' : 'bg-zinc-800 text-zinc-300 hover:bg-zinc-700'}`}
                              >
                                {h === 168 ? '7 días' : `${h}hs`}
                              </button>
                            ))}
                          </div>
                        </div>
                        <p className="text-[10px] text-zinc-500 leading-relaxed pt-1">
                          Se genera un código único (ej: <code className="text-amber-300">PROMO1A2B3C</code>) que aparece en el email y se trackea por conversión en tus métricas.
                        </p>
                      </div>
                    )}
                  </div>
                )}

                <SpamScorePanel campaign={campaign} />

                {/* Schedule mode toggle + datetime input */}
                <div className="rounded-lg border border-zinc-800 bg-zinc-900/50 p-3 space-y-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={scheduleMode}
                      onChange={(e) => setScheduleMode(e.target.checked)}
                      className="w-3.5 h-3.5 accent-violet-500"
                      data-testid="schedule-toggle"
                    />
                    <Calendar size={13} className="text-violet-400" />
                    <span className="text-xs text-zinc-300 font-medium">Programar envío para más tarde</span>
                  </label>
                  {scheduleMode && (
                    <div className="pl-6">
                      <input
                        type="datetime-local"
                        value={scheduledFor}
                        onChange={(e) => setScheduledFor(e.target.value)}
                        min={new Date(Date.now() + 60_000).toISOString().slice(0, 16)}
                        max={new Date(Date.now() + 60 * 24 * 3600_000).toISOString().slice(0, 16)}
                        className="w-full bg-zinc-900 border border-zinc-700 rounded-md px-3 py-2 text-white text-sm"
                        data-testid="schedule-datetime"
                      />
                      <p className="text-[10px] text-zinc-500 mt-1">Tip: martes/jueves entre 18:00–20:00 suelen tener mejor tasa de apertura.</p>
                    </div>
                  )}
                </div>

                <TestEmailButton
                  apiPath="/producers/me/email-campaigns/test-send"
                  subject={campaign.subject}
                  html={campaign.body}
                  className="w-full"
                />

                <button
                  onClick={handleSendCampaign}
                  disabled={sending}
                  className={`w-full px-4 py-2.5 disabled:opacity-50 text-white rounded-lg font-medium text-sm transition-all flex items-center justify-center gap-2 ${scheduleMode ? 'bg-violet-600 hover:bg-violet-700' : 'bg-emerald-600 hover:bg-emerald-700'}`}
                  data-testid="send-campaign-btn"
                >
                  {scheduleMode ? <Calendar size={14} /> : <Send size={14} />}
                  {sending ? 'Procesando…' : scheduleMode ? 'Programar campaña' : 'Enviar ahora'}
                </button>
              </div>

              {/* Live preview */}
              <div data-testid="email-preview">
                <EmailPreviewFrame
                  html={campaign.body || ''}
                  subject={campaign.subject}
                  fromLabel={`${producer?.business_name || producer?.name || 'Tu productora'} <noreply@bravoproductora.com>`}
                />
              </div>
            </div>
          )}
        </div>
        
        {/* By Email Type */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-4">
            <BarChart3 className="w-5 h-5 text-violet-400" />
            <h2 className="text-xl font-semibold text-white">Por Tipo de Email</h2>
          </div>
          
          {stats?.by_type && Object.keys(stats.by_type).length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {Object.entries(stats.by_type).map(([type, data]) => (
                <EmailTypeCard key={type} type={type} data={data} />
              ))}
            </div>
          ) : (
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-8 text-center">
              <Mail className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
              <p className="text-zinc-400">No hay datos de email todavía</p>
              <p className="text-sm text-zinc-500 mt-1">
                Las métricas aparecerán cuando envíes emails a tus compradores
              </p>
            </div>
          )}
        </div>
        
        {/* Info Section */}
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6">
          <div className="flex items-center gap-2 mb-4">
            <Clock className="w-5 h-5 text-violet-400" />
            <h3 className="font-semibold text-white">¿Cómo funciona el tracking?</h3>
          </div>
          <div className="grid md:grid-cols-3 gap-4 text-sm text-zinc-400">
            <div>
              <div className="font-medium text-white mb-1">Enviados</div>
              <p>Cada email que envías a tus compradores se registra automáticamente.</p>
            </div>
            <div>
              <div className="font-medium text-white mb-1">Aperturas</div>
              <p>Se detecta cuando un usuario abre el email (puede ser aproximado por privacidad).</p>
            </div>
            <div>
              <div className="font-medium text-white mb-1">Clicks</div>
              <p>Registramos los clicks en los enlaces del email para medir engagement.</p>
            </div>
          </div>
        </div>
      </div>
    </ProducerLayout>
  );
};

export default ProducerEmailMetricsPage;
